<template>
  <Tooltip placement="top">
    <template #title>
      <span>{{ t('component.table.settingColumn') }}</span>
    </template>
    <Popover
      placement="bottomRight"
      trigger="click"
      :overlayClassName="`${prefixCls}__column-list`"
      :getPopupContainer="getPopupContainer"
      @open-change="onOpenChange"
    >
      <template #title>
        <div :class="`${prefixCls}__popover-title`">
          <Checkbox
            v-model:checked="isColumnAllSelected"
            :indeterminate="indeterminate"
            @change="onColumnAllSelectChange"
          >
            {{ t('component.table.settingColumnShow') }}
          </Checkbox>

          <Checkbox v-model:checked="isIndexColumnShow" @change="onIndexColumnShowChange">
            {{ t('component.table.settingIndexColumnShow') }}
          </Checkbox>
          <!-- 设置了 rowSelection 才出现 -->
          <Checkbox
            v-if="defaultIsRowSelectionShow"
            v-model:checked="isRowSelectionShow"
            @change="onRowSelectionShowChange"
          >
            {{ t('component.table.settingSelectColumnShow') }}
          </Checkbox>
          <!-- <Checkbox
            v-if="defaultIsActionColumnShow"
            v-model:checked="isActionColumnShow"
            @change="onActionColumnShowChange"
          >
            {{ t('component.table.settingActionColumnShow') }}
          </Checkbox> -->

          <MButton size="small" type="link" @click="onReset">
            {{ t('common.resetText') }}
          </MButton>
        </div>
      </template>

      <template #content>
        <ScrollContainer :style="{ 'max-height': tableHeight }">
          <Checkbox.Group ref="columnOptionsRef" v-model:value="columnCheckedOptions">
            <template
              v-for="opt in columnOptions.filter((i) => !i.column.cacheHidden)"
              :key="opt.value"
            >
              <div :class="`${prefixCls}__check-item`" :data-no="opt.value">
                <DragOutlined class="table-column-drag-icon" />
                <Checkbox :value="opt.value"> {{ opt.label }} </Checkbox>

                <Tooltip
                  placement="bottomRight"
                  :mouseLeaveDelay="0.4"
                  :getPopupContainer="getPopupContainer"
                >
                  <template #title>
                    {{ t('component.table.settingFixedLeft') }}
                  </template>
                  <VerticalAlignTopOutlined
                    :rotate="270"
                    :class="[
                      `${prefixCls}__fixed`,
                      {
                        active: opt.fixed === 'left',
                        disabled: opt.value ? !columnCheckedOptions.includes(opt.value) : true,
                      },
                    ]"
                    @click="onColumnFixedChange(opt, 'left')"
                  />
                </Tooltip>
                <Divider type="vertical" />
                <Tooltip
                  placement="bottomRight"
                  :mouseLeaveDelay="0.4"
                  :getPopupContainer="getPopupContainer"
                >
                  <template #title>
                    {{ t('component.table.settingFixedRight') }}
                  </template>
                  <VerticalAlignTopOutlined
                    :rotate="90"
                    :class="[
                      `${prefixCls}__fixed`,
                      {
                        active: opt.fixed === 'right',
                        disabled: opt.value ? !columnCheckedOptions.includes(opt.value) : true,
                      },
                    ]"
                    @click="onColumnFixedChange(opt, 'right')"
                  />
                </Tooltip>
              </div>
            </template>
          </Checkbox.Group>
        </ScrollContainer>
      </template>
      <SettingOutlined />
    </Popover>
  </Tooltip>
</template>
<script lang="ts" setup>
  import { DragOutlined, SettingOutlined, VerticalAlignTopOutlined } from '@ant-design/icons-vue';
  import { Checkbox, Divider, Popover, Tooltip } from 'ant-design-vue';
  import { computed, nextTick, onMounted, ref, unref, useAttrs, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import type { CheckboxChangeEvent } from 'ant-design-vue/es/checkbox/interface';
  import {
    cloneDeep,
    getPopupContainer as getParentContainer,
    isFunction,
    isIfShow,
    isNil,
    omit,
  } from '@mts-fe/utils';
  import { ScrollContainer } from '@mts/components/Container';
  import { TableRowSelection } from '@mts/components/Table/src/types/table';
  import { useDesign } from '@mts/hooks/web/useDesign';
  import { t } from '@mts/hooks/web/useI18n';
  import { usePermission } from '@mts/hooks/web/usePermission';
  import { createSortable } from '@mts/hooks/web/useSortable';
  import { getGlobalParams, getTableCacheKey } from '@mts/render';
  import { mtsEmitter, warn } from '@mts/utils';
  import { getTableSettingStore } from '@/store';
  import { loadTableSetting } from '@/store/modules/tableSetting';
  import { getDataIndex, getTableColumnCache } from '../../helper';
  import { useTableContext } from '../../hooks/useTableContext';
  import type {
    BasicColumn,
    ColumnChangeParam,
    ColumnOptionsType,
    TableSettingCache,
  } from '../../types/table';

  const tableSettingStore = getTableSettingStore();

  defineOptions({ name: 'ColumnSetting' });
  const emit = defineEmits(['columns-change']);

  const { prefixCls } = useDesign('basic-column-setting');

  const attrs = useAttrs();
  const route = useRoute();
  const table = useTableContext();
  const tableId = computed(() => {
    const tableId = table?.getBindValues?.value.tableId;
    if (!tableId) {
      warn('表格缓存功能需要设置 tableId');
      return encodeURIComponent(route.fullPath) + '_TABLE';
    }
    return getTableCacheKey(tableId);
  });

  const props = withDefaults(
    defineProps<{
      /**
       * 是否缓存列的设置
       */
      cache?: boolean;
    }>(),
    {
      cache: () => false,
    },
  );

  const getPopupContainer = () => {
    return isFunction(attrs.getPopupContainer) ? attrs.getPopupContainer() : getParentContainer();
  };

  // 默认值
  let defaultIsIndexColumnShow: boolean = false;
  let defaultIsRowSelectionShow: boolean = false;
  let defaultIsActionColumnShow: boolean = false;
  let defaultRowSelection: TableRowSelection<Recordable<any>>;
  let defaultColumnOptions: ColumnOptionsType[] = [];

  // 是否已经从缓存恢复
  let isRestored = false;
  let isInnerChange = false;

  // 列可选项
  const columnOptions = ref<ColumnOptionsType[]>([]);
  const columnOptionsRef = ref(null);
  // 已选列
  const columnCheckedOptions = ref<string[]>([]);
  // 已选变化
  watch(columnCheckedOptions, () => {
    // 恢复缓存后生效
    if (isRestored) {
      // 显示
      columnOptions.value
        .filter((o) => columnCheckedOptions.value.includes(o.value))
        .forEach((o) => {
          o.column.defaultHidden = false;
        });
      // 隐藏
      columnOptions.value
        .filter((o) => !columnCheckedOptions.value.includes(o.value))
        .forEach((o) => {
          o.column.defaultHidden = true;
          o.fixed = undefined;
        });
      // 从 列可选项 更新 全选状态
      isColumnAllSelectedUpdate();

      // 列表列更新
      tableColumnsUpdate();
    }
  });

  // 全选
  const isColumnAllSelected = ref<boolean>(false);
  const onColumnAllSelectChange = () => {
    if (columnCheckedOptions.value.length < columnOptions.value.length) {
      columnCheckedOptions.value = columnOptions.value.map((o) => o.value);
    } else {
      columnCheckedOptions.value = [];
    }
  };

  // 半选状态
  const indeterminate = computed(() => {
    return (
      columnCheckedOptions.value.length > 0 &&
      columnCheckedOptions.value.length < columnOptions.value.length
    );
  });

  const cacheTableSetting = (setting: Partial<TableSettingCache>) => {
    props.cache && tableId.value && tableSettingStore.setTableSetting(tableId.value, setting);
  };

  // 是否显示序号列
  const isIndexColumnShow = ref<boolean>(false);
  // 序号列更新
  const onIndexColumnShowChange = (e: CheckboxChangeEvent) => {
    // 更新 showIndexColumn
    showIndexColumnUpdate(e.target.checked);
    // 更新 showIndexColumn 缓存
    cacheTableSetting({ showIndexColumn: e.target.checked });
  };

  // 是否显示选择列
  const isRowSelectionShow = ref<boolean>(false);
  // 选择列更新
  const onRowSelectionShowChange = (e: CheckboxChangeEvent) => {
    // 更新 showRowSelection
    showRowSelectionUpdate(e.target.checked);
    // 更新 showRowSelection 缓存
    cacheTableSetting({ showRowSelection: e.target.checked });
  };

  // 是否显示选择列
  const isActionColumnShow = ref<boolean>(false);
  // 选择列更新
  // const onActionColumnShowChange = (e: CheckboxChangeEvent) => {
  //   // 更新 showAction
  //   showActionColumnUpdate(e.target.checked);
  //   // 更新 showAction 缓存
  //   cacheTableSetting({ showActionColumn: e.target.checked });
  // };

  // 更新列缓存
  const columnOptionsSave = (columns = columnOptions.value) => {
    cacheTableSetting({ columns });
  };

  // 重置
  const onReset = () => {
    // 重置默认值
    isIndexColumnShow.value = defaultIsIndexColumnShow;
    // 序号列更新
    onIndexColumnShowChange({
      target: { checked: defaultIsIndexColumnShow },
    } as CheckboxChangeEvent);
    // 重置默认值
    isRowSelectionShow.value = defaultIsRowSelectionShow;
    // 选择列更新
    onRowSelectionShowChange({
      target: { checked: defaultIsRowSelectionShow },
    } as CheckboxChangeEvent);
    // isActionColumnShow.value = defaultIsActionColumnShow;
    // onActionColumnShowChange({
    //   target: { checked: defaultIsActionColumnShow },
    // } as CheckboxChangeEvent);
    // 重置默认值
    columnOptions.value = cloneDeep(defaultColumnOptions);
    // 更新表单状态
    formUpdate();
  };

  // 设置列的 fixed
  const onColumnFixedChange = (opt: ColumnOptionsType, type: 'left' | 'right') => {
    if (type === 'left') {
      if (!opt.fixed || opt.fixed === 'right') {
        opt.fixed = 'left';
      } else {
        opt.fixed = undefined;
      }
    } else if (type === 'right') {
      if (!opt.fixed || opt.fixed === 'left') {
        opt.fixed = 'right';
      } else {
        opt.fixed = undefined;
      }
    }

    // 列表列更新
    tableColumnsUpdate();
  };

  // 沿用逻辑
  const sortableFix = async () => {
    // Sortablejs存在bug，不知道在哪个步骤中会向el append了一个childNode，因此这里先清空childNode
    // 有可能复现上述问题的操作：拖拽一个元素，快速的上下移动，最后放到最后的位置中松手
    if (columnOptionsRef.value) {
      const el = (columnOptionsRef.value as InstanceType<typeof Checkbox.Group>).$el;
      Array.from(el.children).forEach((item) => el.removeChild(item));
    }
    await nextTick();
  };

  const { hasPermission } = usePermission();
  const globalParams = getGlobalParams();
  // 列是否显示逻辑
  const columnIfShow = (column?: Partial<Omit<BasicColumn, 'children'>>) => {
    if (column) {
      if ('auth' in column && !hasPermission(column.auth)) {
        return false;
      }
      if ('ifShow' in column) {
        return isIfShow<BasicColumn>(column, globalParams);
      }
      return true;
    }
    return false;
  };

  // 获取数据列
  const getTableColumns = () => {
    return table
      .getColumns({ ignoreIndex: true, ignoreAction: true, ignoreDynamic: true })
      .filter((col) => columnIfShow(col));
  };

  // 设置列表列
  const tableColumnsSet = (columns: BasicColumn[]) => {
    isInnerChange = true;
    table?.setColumns(columns);

    // 沿用逻辑
    const columnChangeParams: ColumnChangeParam[] = columns.map((col) => ({
      dataIndex: getDataIndex(col.dataIndex),
      fixed: col.fixed,
      width: col.width,
      visible: !col.defaultHidden,
    }));
    emit('columns-change', columnChangeParams);
  };

  // 列表列更新
  const tableColumnsUpdate = () => {
    const columns = cloneDeep(table.getColumns());

    const optKeys = new Set(columnOptions.value.map((o) => o.value));

    // 记录模板（配置列 / 非配置列），排除 ACTION
    const template: Array<'configurable' | 'non-configurable'> = [];
    const nonConfigurableCols: BasicColumn[] = [];
    const configurableMap = new Map<string, BasicColumn>();

    for (const col of columns) {
      const key = getDataIndex(col.dataIndex);
      if (key === 'ACTION') continue;

      if (optKeys.has(key)) {
        template.push('configurable');
        configurableMap.set(key, col);
      } else {
        template.push('non-configurable');
        nonConfigurableCols.push(col);
      }
    }

    // 配置列按 columnOptions 顺序排列并应用设置
    const configurableCols: BasicColumn[] = [];
    for (const opt of columnOptions.value) {
      const col = configurableMap.get(opt.value);
      if (col) {
        col.defaultHidden = opt.column?.defaultHidden;
        col.fixed = opt.fixed;
        col.width = opt.width;
        configurableCols.push(col);
      }
    }

    // 按模板填充：配置列槽位按 columnOptions 顺序，非配置列保持原位
    const result: BasicColumn[] = [];
    let ci = 0;
    let ni = 0;

    for (const slot of template) {
      if (slot === 'configurable' && ci < configurableCols.length) {
        result.push(configurableCols[ci++]);
      } else if (ni < nonConfigurableCols.length) {
        result.push(nonConfigurableCols[ni++]);
      }
    }

    // ACTION 始终在最后
    const actionCol = columns.find((o) => o.dataIndex === 'ACTION');
    if (actionCol) result.push(actionCol);

    tableColumnsSet(result);
    columnOptionsSave();
  };

  const sortable = ref();
  // 打开浮窗
  const onOpenChange = async () => {
    await nextTick();

    if (columnOptionsRef.value && !sortable.value) {
      // 注册排序实例
      const el = (columnOptionsRef.value as InstanceType<typeof Checkbox.Group>).$el;
      sortable.value = createSortable(unref(el), {
        handle: '.table-column-drag-icon ',
        dataIdAttr: 'data-no',
        onEnd: (evt) => {
          const { oldIndex, newIndex } = evt;
          if (isNil(oldIndex) || isNil(newIndex) || oldIndex === newIndex) {
            return;
          }

          const options = cloneDeep(columnOptions.value);

          // 排序
          if (oldIndex > newIndex) {
            options.splice(newIndex, 0, options[oldIndex]);
            options.splice(oldIndex + 1, 1);
          } else {
            options.splice(newIndex + 1, 0, options[oldIndex]);
            options.splice(oldIndex, 1);
          }

          // 更新 列可选项
          columnOptions.value = options;

          // 列表列更新
          tableColumnsUpdate();
        },
      });
    }
  };

  // remove消失的列、push新出现的列
  const diff = () => {
    if (tableId.value) {
      let cache = tableSettingStore.getColumns(tableId.value);
      // value label width 是否一致
      if (
        cache &&
        Array.isArray(cache) &&
        JSON.stringify(
          columnOptions.value.map((o) => ({ value: o.value, label: o.label, width: o.width })),
        ) !== JSON.stringify(cache.map((o) => ({ value: o.value, label: o.label, width: o.width })))
      ) {
        const map = columnOptions.value.reduce((map, item) => {
          map[item.value] = item.label;
          return map;
        }, {});
        // remove消失的列
        cache = cache.filter((o) => map[o.value]);
        // 更新label
        cache.forEach((o) => {
          o.label = map[o.value];
        });
        const cacheKeys = cache.map((o) => o.value);
        // push新出现的列
        cache = cache.concat(columnOptions.value.filter((o) => !cacheKeys.includes(o.value)));

        // 更新列缓存
        columnOptionsSave(cache);
      }
    }
  };

  // 从缓存恢复
  const restore = () => {
    if (tableId.value) {
      const isIndexColumnShowCache = tableSettingStore.getShowIndexColumn(tableId.value);
      // 设置过才恢复
      if (typeof isIndexColumnShowCache === 'boolean') {
        isIndexColumnShow.value = isIndexColumnShowCache;
      }

      const isRowSelectionShowCache = tableSettingStore.getShowRowSelection(tableId.value);
      // 设置过才恢复
      if (typeof isRowSelectionShowCache === 'boolean') {
        isRowSelectionShow.value = defaultIsRowSelectionShow && isRowSelectionShowCache;
      }

      // const isActionColumnShowCache = tableSettingStore.getShowActionColumn(tableId.value);
      // // 设置过才恢复
      // if (typeof isActionColumnShowCache === 'boolean') {
      //   isActionColumnShow.value = defaultIsActionColumnShow && isActionColumnShowCache;
      // }
    }
    // 序号列更新
    onIndexColumnShowChange({
      target: { checked: isIndexColumnShow.value },
    } as CheckboxChangeEvent);
    // 选择列更新
    onRowSelectionShowChange({
      target: { checked: isRowSelectionShow.value },
    } as CheckboxChangeEvent);
    // 操作列更新
    // onActionColumnShowChange({
    //   target: { checked: isActionColumnShow.value },
    // } as CheckboxChangeEvent);

    if (tableId.value) {
      const cache = tableSettingStore.getColumns(tableId.value);
      // 设置过才恢复
      if (Array.isArray(cache)) {
        columnOptions.value = cache;
      }
    }
  };

  // 从 列可选项 更新 已选列
  const columnCheckedOptionsUpdate = () => {
    columnCheckedOptions.value = columnOptions.value
      .filter((o) => !o.column?.defaultHidden)
      .map((o) => o.value);
  };
  // 从 列可选项 更新 全选状态
  const isColumnAllSelectedUpdate = () => {
    isColumnAllSelected.value = columnOptions.value.length === columnCheckedOptions.value.length;
  };
  // 更新 showIndexColumn
  const showIndexColumnUpdate = (showIndexColumn) => {
    isInnerChange = true;
    table.setProps({
      showIndexColumn,
    });
  };
  // 更新 rowSelection
  const showRowSelectionUpdate = (showRowSelection) => {
    isInnerChange = true;
    table.setProps({
      rowSelection: showRowSelection
        ? {
            ...omit(table.getRowSelection(), ['selectedRowKeys']),
            fixed: true,
          }
        : undefined,
    });
  };
  // 更新 ActionColumn
  const showActionColumnUpdate = (showAction: boolean) => {
    isInnerChange = true;
    table.toggleActionColumn(showAction);
  };

  // 更新表单状态
  const formUpdate = () => {
    // 从 列可选项 更新 已选列
    columnCheckedOptionsUpdate();

    // 从 列可选项 更新 全选状态
    isColumnAllSelectedUpdate();

    // 更新 showIndexColumn
    showIndexColumnUpdate(isIndexColumnShow.value);

    // 更新 showRowSelection
    showRowSelectionUpdate(isRowSelectionShow.value);

    // 更新 showActionColumn
    // showActionColumnUpdate(isActionColumnShow.value);

    // 列表列更新
    tableColumnsUpdate();
  };

  const init = async () => {
    if (!isRestored) {
      // 获取数据列
      const columns = getTableColumns();

      // 沿用逻辑
      table.setCacheColumns?.(columns);

      // 生成 默认值
      const options: ColumnOptionsType[] = [];
      for (const col of columns) {
        // 只缓存 string 类型的列
        options.push(getTableColumnCache(col));
      }
      defaultColumnOptions = options;
      columnOptions.value = cloneDeep(options);

      // remove消失的列、push新出现的列
      props.cache && diff();

      // 从缓存恢复
      props.cache && restore();

      // 更新表单状态
      formUpdate();

      columnOptions.value.forEach(({ value, width }) => {
        table?.setCacheColumnsByField?.(value, { width });
      });

      isRestored = true;
    }
  };

  // 初始化：仅执行一次
  const once = async () => {
    // 默认值 缓存
    defaultIsIndexColumnShow = table.getBindValues.value.showIndexColumn || false;
    defaultRowSelection = table.getRowSelection();
    defaultIsRowSelectionShow = !!defaultRowSelection; // 设置了 rowSelection 才出现
    // defaultIsActionColumnShow = !!table.getBindValues.value.actionColumn;
    // 默认值 赋值
    isIndexColumnShow.value = defaultIsIndexColumnShow;
    isRowSelectionShow.value = defaultIsRowSelectionShow;
    // isActionColumnShow.value = defaultIsActionColumnShow;

    await sortableFix();
    await init();

    props.cache &&
      tableId.value &&
      loadTableSetting(tableId.value).then((res) => {
        if (!res) {
          return;
        }

        const current = {
          showIndexColumn: isIndexColumnShow.value,
          showRowSelection: isRowSelectionShow.value,
          // showActionColumn: isActionColumnShow.value,
          columns: columnOptions.value.map(({ label, ...i }) => i),
        };
        const cache = { ...res, columns: res.columns?.map(({ label, ...i }) => i) };
        if (JSON.stringify(cache) !== JSON.stringify(current)) {
          onReset();
          cacheTableSetting(cache);
          isRestored = false;
          init();
        }
      });
  };
  once();

  // 外部列改变
  const getColumns = computed(() => {
    return table?.getColumns();
  });
  const getValues = computed(() => {
    return table?.getBindValues;
  });

  const tableHeight = ref('70vh');
  onMounted(() => {
    const tableEl = table.wrapRef.value?.querySelector('.ant-table-wrapper') as HTMLElement;
    if (tableEl) {
      setTimeout(() => {
        const height = tableEl.offsetHeight - 160;
        tableHeight.value = (height < 200 ? 200 : height) + 'px';
      }, 1000);
    }

    mtsEmitter.on('tableInnerChanged', (flag) => flag && (isInnerChange = true));
    mtsEmitter.on('tableWidthChanged', (col) => {
      isInnerChange = true;
      columnOptions.value.forEach((column) => {
        if (column.value === (getDataIndex(col.dataIndex) || (col.title || '').toString())) {
          column.width = col.width;
        }
      });
      // 更新列缓存
      columnOptionsSave();
    });

    watch([getColumns, getValues], () => {
      if (!isInnerChange) {
        isRestored = false;
        console.log('onMounted isRestored');
        init();
      } else {
        isInnerChange = false;
      }
    });
  });
</script>
<style lang="less">
  @prefix-cls: ~'@{namespace}-basic-column-setting';

  .table-column-drag-icon {
    margin: 0 5px;
    cursor: move;
  }

  .@{prefix-cls} {
    &__popover-title {
      display: flex;
      position: relative;
      align-items: center;
      justify-content: space-between;
    }

    &__check-item {
      display: flex;
      align-items: center;
      min-width: 100%;
      padding: 4px 16px 8px 0;

      .ant-checkbox-wrapper {
        width: 100%;

        &:hover {
          color: @primary-color;
        }
      }
    }

    &__fixed {
      color: rgb(0 0 0 / 45%);
      font-size: 18px;
      cursor: pointer;

      &.active,
      &:hover {
        color: @primary-color;
      }

      &.disabled {
        color: @disabled-color;
        cursor: not-allowed;
      }
    }

    &__column-list {
      svg {
        width: 1em !important;
        height: 1em !important;
      }

      .ant-popover-inner-content {
        // max-height: 360px;
        padding-right: 0;
        padding-left: 0;
        // overflow: auto;
      }

      .ant-checkbox-group {
        display: inline-block;
        width: 100%;
        min-width: 260px;
        // flex-wrap: wrap;
      }
    }
  }
</style>
